// /resources/js/network/ProxySocket.js

import { Mutex } from 'async-mutex';

const closeError = new Error('ProxySocket was closed');

/**
 * This class provides a Promised-based wrapper around our proxy Web Worker.
 * It mimics the interface of `PromisedWebSockets`, allowing it to be used
 * as a drop-in replacement for the `networkSocket` in the `TelegramClient`.
 */
export class ProxySocket {
    constructor() {
        this._worker = null;
        this._buffer = Buffer.alloc(0);
        this._closed = true;
        this._mutex = new Mutex();

        this._canRead = new Promise((resolve) => {
            this._resolveRead = resolve;
        });
    }

    /**
     * --- START: NEW METHOD ---
     * Safely returns the underlying worker instance.
     * @returns {Worker|null} The worker instance or null if not connected.
     */
    getWorker() {
        return this._worker;
    }
    /** --- END: NEW METHOD --- */

    async connect(port, ip, testServers = false) {
        // The URL needs to be constructed with `new URL` for Vite to correctly resolve the worker path.
        this._worker = new Worker(new URL('../workers/telegram.worker.js', import.meta.url));
        this._closed = false;

        const url = port === 443
            ? `wss://${ip}:${port}/apiws${testServers ? '_test' : ''}`
            : `ws://${ip}:${port}/apiws${testServers ? '_test' : ''}`;

        return new Promise((resolve, reject) => {
            this._worker.onmessage = async (event) => {
                const { type, payload } = event.data;

                // Note: The worker now sends a 'heartbeat' message. The ProxySocket ignores it,
                // allowing the main `app-controller` to handle it exclusively.
                if (type === 'message') {
                    const release = await this._mutex.acquire();
                    try {
                        this._buffer = Buffer.concat([this._buffer, Buffer.from(payload)]);
                        this._resolveRead(true);
                    } finally {
                        release();
                    }
                } else if (type === 'open') {
                    console.log('[ProxySocket] Worker reported connection open.');
                    resolve(this);
                } else if (type === 'close' || type === 'error') {
                    console.log('[ProxySocket] Worker reported connection closed or errored.');
                    this._closed = true;
                    this._resolveRead(false); // Unblock any pending reads
                    reject(closeError);
                }
            };

            this._worker.onerror = (error) => {
                console.error('[ProxySocket] Worker reported an unhandled error:', error);
                reject(error);
            };

            this._worker.postMessage({ type: 'connect', payload: { url } });
        });
    }

    isConnected() {
        return !this._closed;
    }

    write(data) {
        if (this._closed) {
            throw closeError;
        }
        this._worker.postMessage({ type: 'send', payload: data });
    }

    async readExactly(number) {
        let readData = Buffer.alloc(0);
        while (readData.length < number) {
            const chunk = await this.read(number - readData.length);
            readData = Buffer.concat([readData, chunk]);
        }
        return readData;
    }

    async read(number) {
        if (this._closed) {
            throw closeError;
        }
        await this._canRead;
        if (this._closed) {
            throw closeError;
        }

        const release = await this._mutex.acquire();
        try {
            const toReturn = this._buffer.slice(0, number);
            this._buffer = this._buffer.slice(number);

            if (this._buffer.length === 0) {
                this._canRead = new Promise((resolve) => {
                    this._resolveRead = resolve;
                });
            }
            return toReturn;
        } finally {
            release();
        }
    }

    async close() {
        if (this._worker) {
            this._worker.postMessage({ type: 'disconnect' });
            this._worker.terminate();
            this._worker = null;
        }
        this._closed = true;
    }
}